import { useState } from 'react';
import { format } from 'date-fns';
import { Edit, Trash2, Users, Eye, CheckCircle, XCircle, Lock } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useWorkplaceCurrency } from '@/contexts/WorkplaceCurrencyContext';
import { useServiceChargeMutations } from '@/hooks/serviceCharge';
import { ServiceChargeEmployeeAssignment } from './ServiceChargeEmployeeAssignment';
import { ServiceChargeDistributionPreview } from './ServiceChargeDistributionPreview';
import type { ServiceChargePeriod } from '@/services/compensation/serviceChargeService';
import { formatCurrencyAmount } from '@/lib/currencyFormat';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface ServiceChargePeriodCardProps {
  period: ServiceChargePeriod;
  onEdit: (periodId: string) => void;
  allPeriods?: ServiceChargePeriod[];
}

export const ServiceChargePeriodCard = ({ period, onEdit, allPeriods = [] }: ServiceChargePeriodCardProps) => {
  const [showAssignment, setShowAssignment] = useState(false);
  const [showDistribution, setShowDistribution] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showActivateDialog, setShowActivateDialog] = useState(false);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const { deletePeriod, activatePeriod, deactivatePeriod } = useServiceChargeMutations();
  const { currencyCode } = useWorkplaceCurrency();

  // Find any currently-active period (other than this one)
  const currentlyActivePeriod = allPeriods.find(
    (p) => p.status === 'active' && p.id !== period.id
  );

  const statusColors = {
    draft: 'secondary',
    active: 'default',
    finalized: 'outline',
  } as const;

  const statusLabels = {
    draft: 'Draft',
    active: 'Active',
    finalized: 'Finalized',
  } as const;

  const handleDelete = () => {
    deletePeriod.mutate(period.id);
    setShowDeleteDialog(false);
  };

  const handleActivateClick = () => {
    if (currentlyActivePeriod) {
      setShowActivateDialog(true);
    } else {
      activatePeriod.mutate(period.id);
    }
  };

  const handleActivateConfirm = () => {
    activatePeriod.mutate(period.id);
    setShowActivateDialog(false);
  };

  const handleDeactivate = () => {
    deactivatePeriod.mutate(period.id);
    setShowDeactivateDialog(false);
  };

  const isLocked = period.status === 'active' || period.status === 'finalized';
  const isFinalized = period.status === 'finalized';

  return (
    <>
      <Card className={
        period.status === 'active' 
          ? 'border-primary/50 shadow-sm' 
          : period.status === 'finalized'
            ? 'border-muted-foreground/30 bg-muted/30'
            : ''
      }>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <CardTitle>{period.period_label}</CardTitle>
                <Badge variant={statusColors[period.status as keyof typeof statusColors]}>
                  {isFinalized && <Lock className="h-3 w-3 mr-1" />}
                  {statusLabels[period.status as keyof typeof statusLabels] || period.status}
                </Badge>
              </div>
              <CardDescription>
                {format(new Date(period.period_start), 'MMM dd, yyyy')} -{' '}
                {format(new Date(period.period_end), 'MMM dd, yyyy')}
                {isFinalized && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    · Locked after payroll completion
                  </span>
                )}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              {period.status === 'draft' && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleActivateClick}
                  disabled={activatePeriod.isPending}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Activate Period
                </Button>
              )}
              {period.status === 'active' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDeactivateDialog(true)}
                  disabled={deactivatePeriod.isPending}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Deactivate
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAssignment(true)}
              >
                <Users className="h-4 w-4 mr-2" />
                {isFinalized ? 'View Assignments' : 'Assign Employees'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDistribution(true)}
              >
                <Eye className="h-4 w-4 mr-2" />
                Preview Distribution
              </Button>
              {!isFinalized && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onEdit(period.id)}
                    disabled={isLocked}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowDeleteDialog(true)}
                    disabled={isLocked}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Total Pool</p>
              <p className="text-2xl font-bold">
                {formatCurrencyAmount(Number(period.total_pool_amount), currencyCode)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">
                Staff ({period.staff_allocation_percent}%)
              </p>
              <p className="text-lg font-semibold text-primary">
                {formatCurrencyAmount(Number(period.staff_allocation_amount), currencyCode)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">
                Loss & Breakage ({period.loss_breakage_percent}%)
              </p>
              <p className="text-lg font-semibold">
                {formatCurrencyAmount(Number(period.loss_breakage_amount), currencyCode)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">
                Training ({period.training_percent}%)
              </p>
              <p className="text-lg font-semibold">
                {formatCurrencyAmount(Number(period.training_amount), currencyCode)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <ServiceChargeEmployeeAssignment
        open={showAssignment}
        onOpenChange={setShowAssignment}
        periodId={period.id}
        periodLabel={period.period_label}
      />

      <ServiceChargeDistributionPreview
        open={showDistribution}
        onOpenChange={setShowDistribution}
        periodId={period.id}
        periodLabel={period.period_label}
        periodStart={period.period_start}
        periodEnd={period.period_end}
      />

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Service Charge Period</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{period.period_label}"? This will also delete all
              weight assignments for this period. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showActivateDialog} onOpenChange={setShowActivateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Activate Service Charge Period</AlertDialogTitle>
            <AlertDialogDescription>
              "{currentlyActivePeriod?.period_label}" is currently the active period. 
              Activating "{period.period_label}" will deactivate it and set it back to draft. 
              Only one period can be active at a time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleActivateConfirm}>
              Deactivate &amp; Activate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showDeactivateDialog} onOpenChange={setShowDeactivateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate Service Charge Period</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to deactivate "{period.period_label}"? 
              It will be set back to draft status and can be edited or reactivated later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeactivate}>
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
